import { FormikForm, useToast } from '@color/continuum';
import { useUrlQueryParams } from '@color/lib';
import { Formik, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { getRequestErrorToastMessage } from 'ui/util/getRequestErrorToastMessage';

import { IVariantClassificationListParams } from '../../types';
import { DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS } from '../constants';

import { VariantClassificationFormFields } from './VariantClassificationFormFields';

const AutoSubmitOnChange = () => {
  const { values, submitForm } = useFormikContext<IVariantClassificationListParams>();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      submitForm();
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [values, submitForm]);

  return null;
};

export const VariantClassificationForm = () => {
  const { queryParams: initialValues, setQueryParams } = useUrlQueryParams<
    keyof IVariantClassificationListParams
  >(DEFAULT_VARIANT_CLASSIFICATION_SEARCH_PARAMS);
  const { showErrorToast } = useToast();

  return (
    <Formik<IVariantClassificationListParams>
      initialValues={initialValues}
      // enableReinitialize allows the form to update when navigating to this page with different
      // query params (e.g., clicking nav menu items that link to the same route with different filters)
      enableReinitialize
      onSubmit={async (paramSearchFields: IVariantClassificationListParams) => {
        try {
          await setQueryParams({ ...paramSearchFields });
        } catch (error) {
          const message = getRequestErrorToastMessage(error);
          showErrorToast(message);
        }
      }}
    >
      <FormikForm>
        <AutoSubmitOnChange />
        <VariantClassificationFormFields />
      </FormikForm>
    </Formik>
  );
};
